import type { EmailScenarioId, EmailSettings } from "@/os/data/types";

/* ============================================================================
   Email scenarios — single source of truth for every transactional /
   notification email the platform can send. Used by:
   • Settings → Emails (enable/disable + preview)
   • Every send-site that needs to check whether a scenario is enabled.
   ============================================================================ */

export type EmailLang = "es" | "en";

export type ScenarioAudience =
  | "admin"
  | "client"
  | "team_member"
  | "invitee";

export type ScenarioCategory =
  | "users"
  | "projects"
  | "team"
  | "quotes"
  | "invoices";

export type ScenarioDelivery = "instant" | "scheduled" | "cron";

export interface EmailScenarioMeta {
  id: EmailScenarioId;
  category: ScenarioCategory;
  audience: ScenarioAudience;
  delivery: ScenarioDelivery;
  /** Default state when no override exists yet. */
  defaultEnabled: boolean;
  copy: {
    es: { name: string; when: string; trigger: string };
    en: { name: string; when: string; trigger: string };
  };
}

export const EMAIL_SCENARIOS: EmailScenarioMeta[] = [
  // ───── Users ──────────────────────────────────────────────────────────
  {
    id: "os-welcome",
    category: "users",
    audience: "invitee",
    delivery: "instant",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Bienvenida al OS",
        when: "Cuando un administrador crea un usuario nuevo en Usuarios.",
        trigger: "Al confirmar el formulario «Nuevo usuario» (equipo, cliente o prospecto).",
      },
      en: {
        name: "OS welcome",
        when: "When an admin creates a new user from the Users page.",
        trigger: "On submit of the New User form (team, client or prospect).",
      },
    },
  },
  {
    id: "os-login-otp",
    category: "users",
    audience: "invitee",
    delivery: "instant",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Código de acceso (OTP)",
        when: "Cuando un usuario solicita un código de 6 dígitos para entrar al OS desde /os/login.",
        trigger: "Al pedir el código en el wizard de login (paso «Email» → «Código»). El código expira en 10 minutos.",
      },
      en: {
        name: "Access code (OTP)",
        when: "When a user requests a 6-digit code to sign in to the OS from /os/login.",
        trigger: "On requesting the code in the login wizard (step «Email» → «Code»). The code expires in 10 minutes.",
      },
    },
  },
  {
    id: "client-team-invitation",
    category: "users",
    audience: "invitee",
    delivery: "instant",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Invitación del cliente a su equipo",
        when: "Cuando un cliente invita a alguien de su equipo a colaborar en uno de sus proyectos.",
        trigger: "Al agregar un miembro desde el Client Hub → Proyecto → Equipo del cliente. No se envía OTP al invitado.",
      },
      en: {
        name: "Client team invitation",
        when: "When a client invites someone from their team to collaborate on one of their projects.",
        trigger: "On adding a member from the Client Hub → Project → Client team. No OTP is sent to the invitee.",
      },
    },
  },
  // ───── Projects ───────────────────────────────────────────────────────
  {
    id: "project-invitation",
    category: "projects",
    audience: "invitee",
    delivery: "instant",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Invitación al proyecto",
        when: "Cuando se invita a alguien al equipo de cliente de un proyecto.",
        trigger: "Al crear una invitación en Proyecto → Equipo del cliente.",
      },
      en: {
        name: "Project invitation",
        when: "When someone is invited to a project's client team.",
        trigger: "On creating an invitation in Project → Client team.",
      },
    },
  },
  {
    id: "project-update",
    category: "projects",
    audience: "client",
    delivery: "instant",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Nueva novedad del proyecto",
        when: "Cuando se publica una novedad para el cliente del proyecto.",
        trigger: "Al crear un «project update» visible al cliente.",
      },
      en: {
        name: "New project update",
        when: "When a new update is posted for the project's client.",
        trigger: "On creating a client-visible project update.",
      },
    },
  },
  // ───── Team ───────────────────────────────────────────────────────────
  {
    id: "team-change",
    category: "team",
    audience: "client",
    delivery: "instant",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Cambio en el equipo del proyecto",
        when: "Cuando se aplica un alta / baja / reemplazo en el equipo del proyecto.",
        trigger: "Al guardar un cambio en Proyecto → Equipo → Smart Manage.",
      },
      en: {
        name: "Project team change",
        when: "When an add / remove / replace is applied to a project team.",
        trigger: "On saving a change in Project → Team → Smart Manage.",
      },
    },
  },
  // ───── Invoices ───────────────────────────────────────────────────────
  {
    id: "invoice-issued",
    category: "invoices",
    audience: "client",
    delivery: "instant",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Factura emitida",
        when: "Cuando se emite una factura nueva al cliente (manual o por cron de facturación automática).",
        trigger: "Al generarse la factura en `auto-generate-invoices` o al emitirla manualmente desde Facturas.",
      },
      en: {
        name: "Invoice issued",
        when: "When a new invoice is issued to the client (manual or via the auto-billing cron).",
        trigger: "On invoice creation in `auto-generate-invoices` or when issued manually from Invoices.",
      },
    },
  },
  {
    id: "invoice-reminder",
    category: "invoices",
    audience: "client",
    delivery: "cron",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Recordatorio de factura vencida",
        when: "Facturas vencidas no pagadas, según la cadencia configurada (diaria, semanal o mensual).",
        trigger: "Cron `send-invoice-reminders` cada día.",
      },
      en: {
        name: "Overdue invoice reminder",
        when: "Past-due unpaid invoices, on the configured cadence (daily, weekly or monthly).",
        trigger: "Cron `send-invoice-reminders` runs daily.",
      },
    },
  },
  {
    id: "payment-status-update",
    category: "invoices",
    audience: "client",
    delivery: "instant",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Estado de pago de factura",
        when: "Cuando finanzas acepta, rechaza o elimina una declaración de pago.",
        trigger: "Al cambiar el estado del pago en Facturas.",
      },
      en: {
        name: "Payment status update",
        when: "When finance accepts, rejects or removes a payment declaration.",
        trigger: "On updating the payment status in Invoices.",
      },
    },
  },
  // ───── Quotes ─────────────────────────────────────────────────────────
  {
    id: "contact-form-admin",
    category: "quotes",
    audience: "admin",
    delivery: "instant",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Formulario de contacto del website — Administrador",
        when: "Cuando un prospecto envía con éxito el formulario de contacto público con su brief.",
        trigger: "Al completar el wizard de /contact y enviar el brief. Se envía a hello@smart-canvas.com con todos los datos capturados.",
      },
      en: {
        name: "Website contact form — Admin",
        when: "When a prospect successfully submits the public contact form with their brief.",
        trigger: "On completing the /contact wizard. Sent to hello@smart-canvas.com with every field captured.",
      },
    },
  },
  {
    id: "contact-form-prospect",
    category: "quotes",
    audience: "client",
    delivery: "instant",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Formulario de contacto del website — Prospecto",
        when: "Cuando un prospecto envía con éxito el formulario de contacto público con su brief.",
        trigger: "Al completar el wizard de /contact. Se envía al prospecto con el resumen de su brief y un enlace seguro para descargar el PDF firmado del NDA mutuo en su idioma.",
      },
      en: {
        name: "Website contact form — Prospect",
        when: "When a prospect successfully submits the public contact form with their brief.",
        trigger: "On completing the /contact wizard. Sent to the prospect with a summary of their brief and a secure link to download the signed mutual NDA PDF in their language.",
      },
    },
  },
  {
    id: "ventures-form-admin",
    category: "quotes",
    audience: "admin",
    delivery: "instant",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Formulario de información del programa de Ventures — Administrador",
        when: "Cuando alguien envía con éxito el formulario de interés del Smart Canvas Venture Program desde /ventures.",
        trigger: "Al completar el wizard de /ventures y aceptar el NDA. Se envía a hello@smart-canvas.com con los datos del interesado (nombre, empresa, rol — Inversor, Founder, etc.).",
      },
      en: {
        name: "Ventures program info form — Admin",
        when: "When someone successfully submits the Smart Canvas Venture Program interest form on /ventures.",
        trigger: "On completing the /ventures wizard and accepting the NDA. Sent to hello@smart-canvas.com with the prospect's data (name, company, role — Investor, Founder, etc.).",
      },
    },
  },
  {
    id: "ventures-form-prospect",
    category: "quotes",
    audience: "client",
    delivery: "instant",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Formulario de información del programa de Ventures — Prospecto",
        when: "Cuando alguien envía con éxito el formulario de interés del Smart Canvas Venture Program desde /ventures.",
        trigger: "Al completar el wizard de /ventures. Se envía al interesado con la confirmación de los próximos pasos (videollamada de presentación) y un enlace seguro para descargar el PDF firmado del NDA mutuo en su idioma.",
      },
      en: {
        name: "Ventures program info form — Prospect",
        when: "When someone successfully submits the Smart Canvas Venture Program interest form on /ventures.",
        trigger: "On completing the /ventures wizard. Sent to the prospect confirming next steps (program presentation video call) and a secure link to download the signed mutual NDA PDF in their language.",
      },
    },
  },
  {
    id: "quote-sent-to-client",
    category: "quotes",
    audience: "client",
    delivery: "instant",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Cotización enviada al cliente",
        when: "Cuando se envía la cotización al prospecto o cliente (cambio de estado a «Enviado»).",
        trigger: "Al cambiar el estado de la cotización a «Enviado» desde el dropdown de Cotizaciones.",
      },
      en: {
        name: "Quote sent to client",
        when: "When the quote is sent to the prospect or client (status changed to «Sent»).",
        trigger: "On changing the quote status to «Sent» from the dropdown in Quotes.",
      },
    },
  },
  {
    id: "quote-admin-open",
    category: "quotes",
    audience: "admin",
    delivery: "instant",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Aviso al admin: cotización abierta",
        when: "Cada vez que un cliente abre una cotización compartida.",
        trigger: "Webhook de seguimiento al renderizar la cotización pública.",
      },
      en: {
        name: "Admin alert: quote opened",
        when: "Every time a client opens a shared quote.",
        trigger: "Tracking webhook fired by the public quote view.",
      },
    },
  },
  {
    id: "quote-admin-accept",
    category: "quotes",
    audience: "admin",
    delivery: "instant",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Aviso al admin: cotización aceptada",
        when: "Cuando el cliente acepta una cotización.",
        trigger: "Acción «Aceptar» en la cotización pública.",
      },
      en: {
        name: "Admin alert: quote accepted",
        when: "When the client accepts a quote.",
        trigger: "«Accept» action on the public quote view.",
      },
    },
  },
  {
    id: "quote-admin-no-view-2d",
    category: "quotes",
    audience: "admin",
    delivery: "cron",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Aviso al admin: 2 días sin abrir",
        when: "Cuando pasaron 2 días desde el envío y el cliente no la ha abierto.",
        trigger: "Cron diario de seguimiento de cotizaciones.",
      },
      en: {
        name: "Admin alert: 2 days, no view",
        when: "When 2 days have passed since send and the client hasn't opened it.",
        trigger: "Daily quote follow-up cron.",
      },
    },
  },
  {
    id: "quote-admin-no-view-4d",
    category: "quotes",
    audience: "admin",
    delivery: "cron",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Aviso al admin: 4 días sin abrir",
        when: "Cuando pasaron 4 días desde el envío y el cliente no la ha abierto.",
        trigger: "Cron diario de seguimiento de cotizaciones.",
      },
      en: {
        name: "Admin alert: 4 days, no view",
        when: "When 4 days have passed since send and the client hasn't opened it.",
        trigger: "Daily quote follow-up cron.",
      },
    },
  },
  {
    id: "quote-client-reminder-2d",
    category: "quotes",
    audience: "client",
    delivery: "cron",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Recordatorio al cliente: 2 días",
        when: "Recordatorio suave 2 días después de enviar la cotización si sigue sin acción.",
        trigger: "Cron diario de seguimiento de cotizaciones.",
      },
      en: {
        name: "Client reminder: 2 days",
        when: "Gentle reminder 2 days after the quote is sent if still untouched.",
        trigger: "Daily quote follow-up cron.",
      },
    },
  },
  {
    id: "quote-client-reminder-4d",
    category: "quotes",
    audience: "client",
    delivery: "cron",
    defaultEnabled: true,
    copy: {
      es: {
        name: "Recordatorio al cliente: 4 días",
        when: "Recordatorio más firme 4 días después de enviar la cotización si sigue sin acción.",
        trigger: "Cron diario de seguimiento de cotizaciones.",
      },
      en: {
        name: "Client reminder: 4 days",
        when: "Firmer reminder 4 days after the quote is sent if still untouched.",
        trigger: "Daily quote follow-up cron.",
      },
    },
  },
];

/** Resolve the enabled state of a scenario, falling back to its default. */
export function isEmailScenarioEnabled(
  settings: EmailSettings | undefined | null,
  id: EmailScenarioId,
): boolean {
  const override = settings?.scenarios?.[id]?.enabled;
  if (typeof override === "boolean") return override;
  const meta = EMAIL_SCENARIOS.find((s) => s.id === id);
  return meta?.defaultEnabled ?? true;
}

export const CATEGORY_ORDER: ScenarioCategory[] = [
  "users",
  "projects",
  "team",
  "quotes",
  "invoices",
];